import { useRef, useState } from "preact/hooks";

import { useModalA11y } from "../hooks/useModalA11y";
import { ApiError, postPasswordChange } from "../lib/api";
import {
  GENERIC_FALLBACK_ERROR,
  PASSWORD_FIELD_LABEL_CHOOSE,
  PASSWORD_TOO_SHORT_ERROR,
  SETTINGS_CURRENT_PASSWORD_LABEL,
  SETTINGS_CURRENT_PASSWORD_MISMATCH_ERROR,
  SETTINGS_PASSWORD_ACTION,
  SETTINGS_SAVE_NEW_PASSWORD_BUTTON,
  passwordCounterText,
  passwordMeetsMinLength,
} from "../lib/authCopy";
import { PasswordToggleInput } from "./PasswordToggleInput";

export interface ChangePasswordSheetProps {
  open: boolean;
  /** Called after a successful `POST /auth/password/change` — the caller
   * (`SignInSettingsView`) closes the sheet and shows the governed
   * `Snackbar` success copy itself, matching `AddPersonSheet`'s same
   * caller-owns-the-close-and-notify contract. */
  onDone: () => void;
  onCancel: () => void;
}

/**
 * Sprint-007 D6 (UX §4.2, ADR-002 A4 §5 — REQUIRES the current password,
 * reversing the UX doc's original session-as-proof proposal). Reuses
 * `AddPersonSheet`'s sheet chrome verbatim (backdrop, Escape, focus trap
 * via `useModalA11y`) — "matching every other sheet in the app," per the
 * UX doc's own instruction. Same live-counter/show-toggle rules as the
 * onboarding password field (UX §1.2), applied only to the NEW password.
 */
export function ChangePasswordSheet({ open, onDone, onCancel }: ChangePasswordSheetProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentPasswordRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Same synchronous-reset-on-open pattern as `AddPersonSheet` — see that
  // component's docstring for the fast-fill race this avoids.
  const wasOpenRef = useRef(open);
  if (open && !wasOpenRef.current) {
    setCurrentPassword("");
    setNewPassword("");
    setSubmitAttempted(false);
    setError(null);
  }
  wasOpenRef.current = open;

  // Sprint-011 C1 (TD-012) — backdrop + Cancel route through the hook's
  // returned `close`, not `onCancel` directly.
  const { close } = useModalA11y(containerRef, {
    active: open,
    onClose: onCancel,
    initialFocusRef: currentPasswordRef,
  });

  if (!open) return null;

  const meetsLength = passwordMeetsMinLength(newPassword.length);

  async function submit() {
    if (saving) return;
    if (!currentPassword || !meetsLength) {
      setSubmitAttempted(true);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await postPasswordChange({ current_password: currentPassword, new_password: newPassword });
      onDone();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError(SETTINGS_CURRENT_PASSWORD_MISMATCH_ERROR);
        setCurrentPassword("");
      } else if (err instanceof ApiError && err.status === 422) {
        setError(PASSWORD_TOO_SHORT_ERROR);
      } else {
        setError(GENERIC_FALLBACK_ERROR);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div class="assign-sheet-backdrop is-open" onClick={close} />
      <div
        ref={containerRef}
        class="assign-sheet is-open"
        role="dialog"
        aria-modal="true"
        aria-labelledby="change-password-title"
      >
        <div class="assign-sheet-handle" aria-hidden="true" />
        <h2 class="assign-sheet-title" id="change-password-title">
          {SETTINGS_PASSWORD_ACTION}
        </h2>

        <label class="admin-field-label" for="change-password-current">
          {SETTINGS_CURRENT_PASSWORD_LABEL}
        </label>
        <PasswordToggleInput
          id="change-password-current"
          value={currentPassword}
          autoComplete="current-password"
          disabled={saving}
          onInput={setCurrentPassword}
        />

        <label class="admin-field-label" for="change-password-new">
          {PASSWORD_FIELD_LABEL_CHOOSE}
        </label>
        <PasswordToggleInput
          id="change-password-new"
          value={newPassword}
          autoComplete="new-password"
          disabled={saving}
          onInput={setNewPassword}
        />
        <p class={`auth-counter${meetsLength ? " is-met" : ""}`}>
          {passwordCounterText(newPassword.length)}
        </p>

        {submitAttempted && !meetsLength ? (
          <p class="error-hint" role="alert">
            {PASSWORD_TOO_SHORT_ERROR}
          </p>
        ) : null}
        {error ? (
          <p class="error-hint" role="alert">
            {error}
          </p>
        ) : null}

        <button
          type="button"
          class="primary-btn"
          style={{ marginTop: "14px" }}
          disabled={!currentPassword || !meetsLength || saving}
          onClick={() => void submit()}
        >
          {SETTINGS_SAVE_NEW_PASSWORD_BUTTON}
        </button>
        <button
          type="button"
          class="secondary-btn"
          style={{ marginTop: "10px" }}
          disabled={saving}
          onClick={close}
        >
          Cancel
        </button>
      </div>
    </>
  );
}
